import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DownloadIcon from '@mui/icons-material/Download'
import {
  useImportCollectionMutation,
  usePreviewCollectionImportMutation,
  CollectionImportPreview,
} from '@store/api/importApi'
import { useAppSelector } from '@store/hooks'
import PageHeader from '@components/layout/PageHeader'
import { ResponsiveTable } from '@components/layout/ResponsiveTable'

const MAX_FILE_BYTES = 2 * 1024 * 1024 // 2 MB, must match server limit

const ImportData: React.FC = () => {
  const { token } = useAppSelector((state) => state.auth)
  const [csv, setCsv] = useState('')
  const [preview, setPreview] = useState<CollectionImportPreview | null>(null)
  const [duplicateStrategy, setDuplicateStrategy] = useState<'merge' | 'skip'>('merge')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [previewImport, { isLoading: isPreviewing }] = usePreviewCollectionImportMutation()
  const [importCollection, { isLoading: isImporting }] = useImportCollectionMutation()

  const handleFile = async (file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      setCsv('')
      setPreview(null)
      setMessage({ type: 'error', text: `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 2 MB.` })
      return
    }
    const text = await file.text()
    setCsv(text)
    setPreview(null)
    setMessage(null)
  }

  const handlePreview = async (suppressMessageReset = false) => {
    if (!csv.trim()) return
    try {
      const data = await previewImport({ csv }).unwrap()
      setPreview(data.preview)
      if (!suppressMessageReset) {
        setMessage(null)
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to preview import file.' })
    }
  }

  const handleImport = async () => {
    if (!csv.trim()) return
    try {
      const data = await importCollection({ csv, duplicateStrategy, skipInvalid: true }).unwrap()
      setMessage({
        type: 'success',
        text: `Imported ${data.result.imported}, merged ${data.result.merged}, skipped ${data.result.skipped}.`,
      })
      await handlePreview(true)
    } catch {
      setMessage({ type: 'error', text: 'Import failed. Check your CSV and try again.' })
    }
  }

  const handleDuplicateStrategyChange = (event: SelectChangeEvent) => {
    setDuplicateStrategy(event.target.value as 'merge' | 'skip')
  }

  const downloadTemplate = () => {
    const link = document.createElement('a')
    link.href = '/api/v1/import/collection/template'
    link.setAttribute('download', 'cigartrack-collection-import-template.csv')
    if (token) {
      fetch('/api/v1/import/collection/template', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          link.href = url
          link.click()
          URL.revokeObjectURL(url)
        })
      return
    }
    link.click()
  }

  const previewColumns = [
    { id: 'row', header: 'Row', renderCell: (row: { rowNumber: number }) => row.rowNumber },
    { id: 'brand', header: 'Brand', renderCell: (row: { brand: string }) => row.brand },
    { id: 'line', header: 'Line', renderCell: (row: { cigarLine: string }) => row.cigarLine },
    { id: 'vitola', header: 'Vitola', renderCell: (row: { vitola: string }) => row.vitola },
    { id: 'qty', header: 'Qty', renderCell: (row: { quantity: number }) => row.quantity },
    {
      id: 'status',
      header: 'Status',
      renderCell: (row: {
        valid: boolean
        duplicateOfExisting?: boolean
        issues: string[]
      }) => {
        if (!row.valid) return 'Invalid'
        if (row.duplicateOfExisting) return 'Duplicate (merge)'
        const base = 'Ready'
        return row.issues.length > 0 ? `${base} — ${row.issues.join(', ')}` : base
      },
    },
  ]

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <PageHeader title="Import" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a CSV with columns <strong>brand</strong>, <strong>cigarLine</strong>, and <strong>vitola</strong>.
        See <code>docs/collection-import-csv.md</code> for the full format.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
              Choose CSV
              <input
                hidden
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void handleFile(file)
                }}
              />
            </Button>
            <Button variant="text" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
              Download template
            </Button>
            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 220 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel id="duplicate-strategy-label">Duplicates</InputLabel>
              <Select
                labelId="duplicate-strategy-label"
                value={duplicateStrategy}
                label="Duplicates"
                onChange={handleDuplicateStrategyChange}
              >
                <MenuItem value="merge">Merge quantities</MenuItem>
                <MenuItem value="skip">Skip duplicates</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" disabled={!csv || isPreviewing} onClick={() => void handlePreview()}>
              Preview
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={!preview || preview.validRows === 0 || isImporting}
              onClick={() => void handleImport()}
            >
              Import valid rows
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${preview.totalRows} rows`} />
              <Chip color="success" label={`${preview.validRows} valid`} />
              <Chip color="warning" label={`${preview.invalidRows} invalid`} />
              <Chip color="info" label={`${preview.duplicateRows} duplicates`} />
            </Stack>

            {preview.headerErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {preview.headerErrors.join('; ')}
              </Alert>
            )}

            <ResponsiveTable
              rows={preview.rows}
              columns={previewColumns}
              getRowId={(row) => String(row.rowNumber)}
              data-testid="import-preview-table"
            />
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default ImportData
